<template>
  <Dialog
    v-model:visible="displayModal"
    class="choose-variants-modal"
    :header="t('templateBuilder.modals.variantSelector.title')"
    :modal="true"
    :dismissable-mask="true"
    :style="{ width: '62vw' }"
    :breakpoints="{ '960px': '85vw', '640px': '96vw' }"
    @update:visible="handleClose"
  >
    <div class="product-context">
      <span class="context-id">#{{ product?.[fields.elementId] }}</span>
      <span class="context-name">{{ product?.[fields.label] }}</span>
    </div>

    <div class="search-container mb-3">
      <span class="p-input-icon-left w-full">
        <i class="fa-thin fa-magnifying-glass" />
        <InputText
          v-model="searchValue"
          :placeholder="t('templateBuilder.modals.variantSelector.searchPlaceholder')"
          class="w-full"
          @input="handleSearchInput"
        />
      </span>
    </div>
    <div class="multi-select-hint">
      <i class="fa-light fa-circle-info" />
      <span>{{ t('templateBuilder.modals.variantSelector.multiSelectHint') }}</span>
    </div>

    <div class="variant-layout">
      <div class="variant-pane available-pane">
        <div
          class="variant-row product-row"
          :class="{ selected: localSelectProduct }"
          @click="toggleSelectProduct"
        >
          <div class="variant-thumb">
            <img
              v-if="getProductImage()"
              :src="getProductImage()"
              :alt="String(product?.[fields.label] || '')"
            >
            <i
              v-else
              class="fa-thin fa-image"
            />
          </div>
          <div class="row-content">
            <span class="row-title">{{ t('templateBuilder.modals.variantSelector.productLabel') }}</span>
            <span class="row-subtitle">{{ product?.[fields.label] }}</span>
          </div>
          <i
            v-if="localSelectProduct"
            class="fa-solid fa-check row-selected-icon"
          />
        </div>

        <div class="variants-title">
          {{ t('templateBuilder.modals.variantSelector.availableVariants', { count: variants.length }) }}
        </div>

        <div
          v-if="isLoading"
          class="empty-state"
        >
          <i class="fa-solid fa-spinner fa-spin fa-2x mb-2" />
          <p>{{ t('templateBuilder.modals.loadingVariants') }}</p>
        </div>

        <div
          v-else-if="variants.length === 0"
          class="empty-state"
        >
          <i class="fa-thin fa-box-open fa-2x mb-2" />
          <p>{{ t('templateBuilder.modals.variantSelector.noResult') }}</p>
        </div>

        <div
          v-else
          ref="availableListRef"
          class="variants-list"
          @scroll="handleAvailableScroll"
        >
          <div
            v-for="variant in variants"
            :key="variant.id_shop_product_attribute"
            class="variant-row"
            :class="{ selected: isVariantSelected(variant.id_shop_product_attribute) }"
            @click="toggleVariant(variant)"
          >
            <div class="variant-thumb">
              <img
                v-if="getVariantImage(variant)"
                :src="getVariantImage(variant)"
                :alt="getVariantLabel(variant)"
              >
              <i
                v-else
                class="fa-thin fa-image"
              />
            </div>
            <div class="row-content">
              <span class="row-title">#{{ variant.id_combination }}</span>
              <span class="row-subtitle">{{ getVariantLabel(variant) }}</span>
            </div>
            <i
              v-if="isVariantSelected(variant.id_shop_product_attribute)"
              class="fa-solid fa-check row-selected-icon"
            />
          </div>
          <div
            v-if="isLoadingMore"
            class="loading-more"
          >
            <i class="fa-solid fa-spinner fa-spin" />
            <span>{{ t('templateBuilder.modals.loadingMore') }}</span>
          </div>
          <div
            v-if="!hasMoreResults && variants.length > 0"
            class="end-of-list"
          >
            <span>{{ t('templateBuilder.modals.endOfList') }}</span>
          </div>
        </div>
      </div>

      <div class="variant-pane selected-pane">
        <div class="selected-header">
          <span>{{ t('templateBuilder.modals.variantSelector.selected') }}</span>
          <span class="count-badge">{{ selectedCount }}</span>
        </div>

        <div
          v-if="selectedCount === 0"
          class="empty-state"
        >
          <i class="fa-thin fa-clipboard-list fa-2x mb-2" />
          <p>{{ t('templateBuilder.modals.variantSelector.noSelection') }}</p>
        </div>

        <div
          v-else
          class="selected-list"
        >
          <div
            v-if="localSelectProduct"
            class="selected-chip selected-product"
          >
            <i class="fa-solid fa-box mr-2" />
            {{ product?.[fields.label] }}
          </div>

          <div
            v-for="variant in selectedVariants"
            :key="variant.id_shop_product_attribute"
            class="selected-chip"
          >
            <i class="fa-solid fa-tag mr-2" />
            #{{ variant.id_combination }} - {{ getVariantLabel(variant) }}
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="footer-content">
        <span class="selection-summary">
          {{ t('templateBuilder.modals.variantSelector.itemsSelected', { count: selectedCount }) }}
        </span>
        <div class="flex gap-2">
          <Button
            :label="t('templateBuilder.modals.cancel')"
            class="p-button-secondary"
            @click="handleClose"
          />
          <Button
            :label="t('templateBuilder.modals.validate')"
            class="validate-btn"
            @click="handleValidate"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  computed, ComputedRef, defineComponent, PropType, ref, Ref, watch,
} from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { StringMap } from '@/types';

interface CombinationItem {
  id_shop_product_attribute: number;
  id_shop_product: number;
  id_combination: number;
  combination_name: string;
  reference: string;
  price: number;
  price_discount: number;
  quantity: number;
  image_link?: string;
}

export default defineComponent({
  name: 'ChooseVariantsModal',
  components: {
    Dialog,
    InputText,
    Button,
  },
  props: {
    displayDialog: {
      type: Boolean,
      required: true,
    },
    product: {
      type: Object as PropType<StringMap | null>,
      required: false,
      default: null,
    },
    variants: {
      type: Array as PropType<CombinationItem[]>,
      required: false,
      default: () => [],
    },
    fields: {
      type: Object as PropType<StringMap>,
      required: true,
    },
    initialProductSelected: {
      type: Boolean,
      required: false,
      default: false,
    },
    initialSelectedVariantIds: {
      type: Array as PropType<CombinationItem[]>,
      required: false,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLoadingMore: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasMoreResults: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: {
    'on-close-dialog': null,
    'on-validate-selection': Object,
    'on-search-change': String,
    'on-load-more': null,
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const displayModal: ComputedRef<boolean> = computed(() => props.displayDialog);
    const searchValue: Ref<string> = ref('');
    const localSelectProduct = ref(false);
    const selectedVariantMap: Ref<Record<string, CombinationItem>> = ref({});
    const availableListRef = ref<HTMLElement | null>(null);

    const resetLocalState = () => {
      searchValue.value = '';
      localSelectProduct.value = props.initialProductSelected;
      selectedVariantMap.value = {};
      if (props.initialProductSelected) {
        return;
      }
      props.initialSelectedVariantIds.forEach((variant) => {
        selectedVariantMap.value[String(variant.id_shop_product_attribute)] = variant;
      });
    };

    watch(
      () => props.displayDialog,
      (visible) => {
        if (visible) {
          resetLocalState();
        }
      },
      { immediate: true },
    );

    const selectedVariants = computed(() => Object.values(selectedVariantMap.value));

    const selectedCount = computed(
      () => (localSelectProduct.value ? 1 : selectedVariants.value.length),
    );

    const isVariantSelected = (variantId: number) => Boolean(selectedVariantMap.value[String(variantId)]);

    const toggleSelectProduct = () => {
      localSelectProduct.value = !localSelectProduct.value;
      if (localSelectProduct.value) {
        selectedVariantMap.value = {};
      }
    };

    const toggleVariant = (variant: CombinationItem) => {
      const key = String(variant.id_shop_product_attribute);
      if (selectedVariantMap.value[key]) {
        delete selectedVariantMap.value[key];
        return;
      }
      if (localSelectProduct.value) {
        localSelectProduct.value = false;
      }
      selectedVariantMap.value[key] = variant;
    };

    const getVariantLabel = (variant: CombinationItem) => variant.combination_name
      || variant.reference
      || `#${variant.id_combination}`;

    const getProductImage = (): string => String(
      props.product?.image_link
      ?? props.product?.image_url
      ?? '',
    ).trim();

    const getVariantImage = (variant: CombinationItem): string => String(
      variant.image_link
      ?? '',
    ).trim();

    const handleSearchInput = () => {
      emit('on-search-change', searchValue.value);
    };

    const handleAvailableScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        emit('on-load-more');
      }
    };

    const handleClose = () => {
      emit('on-close-dialog');
    };

    const handleValidate = () => {
      emit('on-validate-selection', {
        selectProduct: localSelectProduct.value,
        variants: selectedVariants.value,
      });
    };

    return {
      t,
      displayModal,
      searchValue,
      localSelectProduct,
      availableListRef,
      selectedVariants,
      selectedCount,
      isVariantSelected,
      toggleSelectProduct,
      toggleVariant,
      getVariantLabel,
      getProductImage,
      getVariantImage,
      handleSearchInput,
      handleAvailableScroll,
      handleClose,
      handleValidate,
    };
  },
});
</script>

<style scoped lang="scss">
@import '@/assets/scss/utilities/_variables.scss';

.choose-variants-modal {
  .p-dialog-content {
    padding-top: 0.25rem;
    max-height: 78vh;
    overflow: hidden;
  }
}

.product-context {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;

  .context-id {
    font-size: 0.75rem;
    color: $mid-grey;
    font-weight: 600;
  }

  .context-name {
    font-size: 0.95rem;
    font-weight: 600;
    color: $tuna;
  }
}

.search-container {
  .p-inputtext {
    padding-left: 2.5rem;
  }
}

.multi-select-hint {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: $mid-grey;
  font-size: 0.82rem;
  margin-bottom: 0.7rem;

  i {
    color: darken($mid-grey, 5%);
  }
}

.variant-layout {
  display: flex;
  gap: 1rem;
  height: 56vh;
  min-height: 360px;
}

.variant-pane {
  border: 1px solid $solitude;
  border-radius: 10px;
  background: $white;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;

  &.available-pane {
    width: 60%;
    padding: 0.75rem;
  }

  &.selected-pane {
    width: 40%;
    padding: 0.75rem;
  }
}

.variants-title,
.selected-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.82rem;
  font-weight: 600;
  color: $mid-grey;
  margin: 0.35rem 0 0.55rem;
}

.count-badge {
  background: lighten($brand-color-primary, 35%);
  color: darken($brand-color-primary, 15%);
  border-radius: 999px;
  padding: 0.12rem 0.5rem;
  font-size: 0.75rem;
}

.variants-list,
.selected-list {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
  min-height: 0;
}

.loading-more,
.end-of-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  color: $mid-grey;
  font-size: 0.85rem;
}

.variant-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  border: 1px solid $solitude;
  border-radius: 8px;
  padding: 0.55rem 0.7rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: darken($solitude, 8%);
    background: $extra-light-grey;
  }

  &.selected {
    border-color: lighten($brand-color-primary, 20%);
    background: lighten($brand-color-primary, 42%);
  }

  .row-content {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .row-title {
    font-size: 0.82rem;
    color: $mid-grey;
    font-weight: 600;
  }

  .row-subtitle {
    font-size: 0.9rem;
    color: $tuna;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.variant-thumb {
  width: 34px;
  height: 34px;
  border-radius: 6px;
  background: $extra-light-grey;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  color: $mid-grey;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.row-selected-icon {
  color: darken($brand-color-primary, 8%);
  font-size: 0.85rem;
  margin-left: auto;
}

.product-row {
  margin-bottom: 0.25rem;
}

.selected-chip {
  border-radius: 999px;
  background: $extra-light-grey;
  color: $tuna;
  font-size: 0.82rem;
  padding: 0.35rem 0.65rem;
  display: inline-flex;
  align-items: center;
  line-height: 1.2;
  width: fit-content;
  max-width: 100%;
}

.selected-product {
  background: lighten($brand-color-primary, 38%);
  color: darken($brand-color-primary, 18%);
}

.empty-state {
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: $mid-grey;
  text-align: center;
  padding: 1rem;

  p {
    margin: 0;
    font-size: 0.9rem;
  }
}

.footer-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.selection-summary {
  font-size: 0.9rem;
  color: $mid-grey;
}

.validate-btn {
  background: $brand-color-primary;
  border-color: $brand-color-primary;

  &:hover {
    background: $successButtonHoverBg;
    border-color: $successButtonHoverBg;
  }
}
</style>
